import React, { useContext, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faArrowUp, faArrowDown, faCode, faPaste, faTable, faFileImport, faDatabase, faRandom, faCodeBranch } from '@fortawesome/free-solid-svg-icons';
import './styles/CustomizedVariable.css';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import VariableContext from '../contexts/VariableContext';

const CustomizedVariable = ({ isOpen, onClose, setcurrentSelectedVariablesset, projectId }) => {
  const { dataGroup, setdataGroup, selectedProject, selectedClient, powner } = useContext(VariableContext);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  // ── Single-variable state ──────────────────────────────────────────────────
  const [rows, setRows] = useState([{ stub: '', logic: '' }]);
  const [variableName, setVariableName] = useState('');
  const [loopVarName, setLoopVarName] = useState('');
  const [availableVariables, setAvailableVariables] = useState([]);
  const [showVariableSelector, setShowVariableSelector] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const [operationType, setOperationType] = useState('OR');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [loopVar, setLoopVar] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteData, setPasteData] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isSavingDG, setIsSavingDG] = useState(false);
  const [pendingEntries, setPendingEntries] = useState({});
  const fileInputRef = useRef(null);

  // ── Bulk-import state ──────────────────────────────────────────────────────
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRows, setBulkRows] = useState([{ varName: '', condition: '', description: '' }]);
  const [showBulkPasteModal, setShowBulkPasteModal] = useState(false);
  const [bulkPasteData, setBulkPasteData] = useState('');
  const bulkFileInputRef = useRef(null);

  // ── Recode-variables state ─────────────────────────────────────────────────
  const [recodeMode, setRecodeMode] = useState(false);
  const [recodeRows, setRecodeRows] = useState([{ newVarName: '', recodeSpec: '' }]);
  const [showRecodePasteModal, setShowRecodePasteModal] = useState(false);
  const [recodePasteData, setRecodePasteData] = useState('');
  const recodeFileInputRef = useRef(null);

  // ── Conditional-compute state ──────────────────────────────────────────────
  const [conditionalMode, setConditionalMode] = useState(false);
  const [conditionalRows, setConditionalRows] = useState([{ condition: '', varName: '', calculation: '', description: '' }]);
  const [showConditionalPasteModal, setShowConditionalPasteModal] = useState(false);
  const [conditionalPasteData, setConditionalPasteData] = useState('');
  const conditionalFileInputRef = useRef(null);

  // ── SPSS-compare state ─────────────────────────────────────────────────────
  const [spssCompareMode, setSpssCompareMode] = useState(false);
  const [spssRows, setSpssRows] = useState([{ varName: '', condition: '', value: '1', description: '' }]);
  const [showSpssPasteModal, setShowSpssPasteModal] = useState(false);
  const [spssPasteData, setSpssPasteData] = useState('');
  const spssFileInputRef = useRef(null);

  useEffect(() => {
    const dgKeys = dataGroup ? Object.keys(dataGroup) : [];
    const pendingKeys = Object.keys(pendingEntries);
    const allKeys = [...new Set([...dgKeys, ...pendingKeys])];
    setAvailableVariables(allKeys.filter(v => !v.startsWith('_')));
  }, [dataGroup, pendingEntries]);

  if (!isOpen) return null;

  // ── Single-variable helpers ────────────────────────────────────────────────
  const handleAddRow = () => setRows([...rows, { stub: '', logic: '' }]);

  const handleRemoveRow = (index) => {
    if (rows.length > 1) {
      const newRows = [...rows];
      newRows.splice(index, 1);
      setRows(newRows);
    }
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newRows = [...rows];
      [newRows[index], newRows[index - 1]] = [newRows[index - 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleMoveDown = (index) => {
    if (index < rows.length - 1) {
      const newRows = [...rows];
      [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const openVariableSelector = (index) => {
    setCurrentRowIndex(index);
    setShowVariableSelector(true);
    setSelectedVariables([]);
  };

  const toggleVariableSelection = (variable) => {
    setSelectedVariables(prev =>
      prev.includes(variable) ? prev.filter(v => v !== variable) : [...prev, variable]
    );
  };

  const applyVariableOperation = () => {
    if (selectedVariables.length === 0) return;
    let logicExpression = '';
    if (advancedMode) {
      logicExpression = rows[currentRowIndex].logic;
    } else {
      const operator = operationType === 'OR' ? ' or ' : operationType === 'AND' ? ' and ' : ' and not ';
      logicExpression = selectedVariables
        .map((v, i) => {
          if (operationType === 'NOT' && i === selectedVariables.length - 1) return `not ${v}`;
          return v;
        })
        .join(operator);
    }
    const newRows = [...rows];
    newRows[currentRowIndex].logic = logicExpression;
    setRows(newRows);
    setShowVariableSelector(false);
  };

  // Parse for single-variable paste (2 cols)
  const parsePastedData = (text) => {
    const lines = text.trim().split(/\r?\n/);
    const parsedRows = [];
    for (const line of lines) {
      let columns = line.split(/\t/);
      if (columns.length === 1) columns = line.split(/,/);
      const stub = columns[0] ? columns[0].trim() : '';
      const logic = columns[1] ? columns[1].trim() : '';
      if (stub || logic) parsedRows.push({ stub, logic });
    }
    return parsedRows;
  };

  const handlePasteFromExcel = async () => {
    if (!pasteData.trim()) { toast.warning('Please paste some data first'); return; }
    const newRows = parsePastedData(pasteData);
    if (newRows.length === 0) { toast.warning('No valid data found. Please ensure your data has at least one column.'); return; }
    const replaceExisting = await confirm({
      title: "Apply Paste",
      message: `Found ${newRows.length} row(s) to paste. Replace existing rows or append?`,
      confirmText: "Replace",
      cancelText: "Append",
    });
    if (replaceExisting) setRows(newRows);
    else setRows([...rows, ...newRows]);
    setShowPasteModal(false);
    setPasteData('');
    toast.success(`Successfully added ${newRows.length} row(s)!`);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setPasteData(content);
      const previewRows = parsePastedData(content);
      if (previewRows.length > 0) toast.info(`Loaded ${previewRows.length} row(s) from file. Click "Apply Paste" to confirm.`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleSave = async () => {
    setSaveError(null);
    if (!variableName || variableName.trim() === '') { toast.warning('Please enter a variable name'); return; }
    let isRecalculate = false;
    if ((dataGroup && dataGroup[variableName]) || pendingEntries[variableName]) {
      const ok = await confirm({
        title: 'Variable Already Exists',
        message: `Variable "${variableName}" already exists. Do you want to recalculate it with the current logic?`,
        confirmText: 'Recalculate',
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (!ok) return;
      isRecalculate = true;
    }
    const emptyRows = rows.filter(row => !row.stub.trim() || !row.logic.trim());
    if (emptyRows.length > 0) {
      const ok = await confirm({
        title: "Empty Rows",
        message: `${emptyRows.length} row(s) have empty Group Label or Logic Definition. Do you want to continue anyway?`,
        confirmText: "Continue",
        variant: "warning",
      });
      if (!ok) return;
    }
    setIsSaving(true);
    try {
      let maxLoc = 0;
      const allDGSingle = { ...(dataGroup || {}), ...pendingEntries };
      Object.values(allDGSingle).forEach(q => {
        (q?.varName || []).forEach(v => {
          if (v.Location && v.Location.includes(":")) {
            const [, end] = v.Location.split(":").map(Number);
            if (end > maxLoc) maxLoc = end;
          }
        });
      });
      const customVariable = {
        name: variableName.trim(),
        type: 'SingleResponse',
        logic: rows.map(row => ({ stub: row.stub.trim(), condition: row.logic.trim() }))
      };
      if (!isRecalculate) {
        setcurrentSelectedVariablesset(prev => [...prev, customVariable.name]);
      }
      const valueLabels = {};
      const values = rows.map((row, index) => {
        const value = (index + 1).toString() + ".0";
        valueLabels[value] = row.stub;
        return { index: index + 1, title: row.stub };
      });
      let response = null;
      try {
        const { data } = await apiService.post('/compute-newvar', {
          projectId, customVariable, dataGroup,
          project: JSON.stringify(selectedProject),
          client: JSON.stringify(selectedClient),
          user: powner,
          recalculate: isRecalculate,
        }, { timeout: 300000 }); // 5-min timeout — SPSS write + OneDrive upload can be slow
        response = { data };
        if (!data || !data.success) throw new Error(data?.message || 'Failed to create custom variable on server');
      } catch (err) {
        throw new Error(err.message || 'An unexpected error occurred');
      }
      if (response && response.data && response.data.success) {
        const existingEntry = dataGroup?.[variableName] || pendingEntries[variableName];
        const varNameEntry = existingEntry?.varName || [{ Name: variableName, Location: `${maxLoc + 1}:${maxLoc + 2}` }];
        const existingOrder = existingEntry?._order;
        const newOrder = existingOrder || (Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + 1);
        setPendingEntries(prev => ({
          ...prev,
          [variableName]: {
            "Question number": variableName,
            Qtitle: variableName,
            instructions: "",
            type: "SingleResponse",
            values,
            valueLabels,
            varName: varNameEntry,
            "order": newOrder,
            "_order": newOrder,
          },
        }));
        toast.success(isRecalculate
          ? `Variable "${variableName}" recalculated successfully!`
          : `Custom variable "${variableName}" created successfully!`);
      } else {
        throw new Error(response?.data?.message || 'Failed to create custom variable');
      }
    } catch (error) {
      console.error('Error creating custom variable:', error);
      setSaveError(error.message);
      toast.error(error.message || 'Error creating custom variable');
      if (!isRecalculate) {
        setcurrentSelectedVariablesset(prev => prev.filter(v => v !== variableName));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const testBackendConnection = async () => {
    try { await apiService.get('/health', { timeout: 5000 }); return true; }
    catch { return false; }
  };

  const retrySave = async () => { if (saveError) { setSaveError(null); await handleSave(); } };

  // Save the current in-memory dataGroup structure back to the database (qgroup_json).
  // Called automatically after variable creation, and also available as a manual button.
  const handleSaveDataGroup = async () => {
    setIsSavingDG(true);
    try {
      const mergedDG = { ...dataGroup, ...pendingEntries };
      const { data } = await apiService.post('/save_all_data_list', { projectId, dataGroup: mergedDG });
      if (data?.message) {
        setdataGroup(mergedDG);
        setPendingEntries({});
        toast.success('Variable structure saved to database');
      }
    } catch (error) {
      toast.error('Failed to save structure: ' + error.message);
    } finally {
      setIsSavingDG(false);
    }
  };

  // ── Bulk-import helpers ────────────────────────────────────────────────────
  const parseBulkPastedData = (text) => {
    const lines = text.trim().split(/\r?\n/);
    const parsedRows = [];
    for (const line of lines) {
      let columns = line.split(/\t/);
      if (columns.length === 1) columns = line.split(/,/);
      const varName    = columns[0] ? columns[0].trim() : '';
      const condition  = columns[1] ? columns[1].trim() : '';
      const description = columns[2] ? columns[2].trim() : '';
      if (varName || condition) parsedRows.push({ varName, condition, description });
    }
    return parsedRows;
  };

  const handleApplyBulkPaste = () => {
    if (!bulkPasteData.trim()) { toast.warning('Please paste some data first'); return; }
    const newRows = parseBulkPastedData(bulkPasteData);
    if (newRows.length === 0) { toast.warning('No valid data found.'); return; }
    setBulkRows(newRows);
    setShowBulkPasteModal(false);
    setBulkPasteData('');
    toast.success(`Parsed ${newRows.length} variable(s) — review and save when ready.`);
  };

  const handleBulkFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setBulkPasteData(content);
      const preview = parseBulkPastedData(content);
      if (preview.length > 0) toast.info(`Loaded ${preview.length} variable(s) from file.`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleBulkRowChange = (index, field, value) => {
    const updated = [...bulkRows];
    updated[index] = { ...updated[index], [field]: value };
    setBulkRows(updated);
  };

  const handleBulkAddRow = () =>
    setBulkRows([...bulkRows, { varName: '', condition: '', description: '' }]);

  const handleBulkRemoveRow = (index) => {
    if (bulkRows.length > 1) {
      const updated = [...bulkRows];
      updated.splice(index, 1);
      setBulkRows(updated);
    }
  };

  const handleBulkSave = async () => {
    // Rows that have both a name AND a condition are "ready to send"
    const readyRows = bulkRows.filter(r => r.varName.trim() && r.condition.trim());
    if (readyRows.length === 0) {
      toast.warning('Please add at least one variable with both a name and a condition');
      return;
    }

    setIsSaving(true);
    try {
      const { data } = await apiService.post('/compute-newvar-bulk', {
        projectId,
        bulkVariables: readyRows.map(r => ({
          varName:     r.varName.trim(),
          condition:   r.condition.trim(),
          description: r.description.trim(),
        })),
        dataGroup,
        project: JSON.stringify(selectedProject),
        client:  JSON.stringify(selectedClient),
        user:    powner,
      }, { timeout: 300000 }); // 5-min timeout — N variables × SPSS write + OneDrive + PG sync

      if (!data || !data.success) {
        throw new Error(data?.error || 'Failed to import variables');
      }

      const createdVars = data.created || [];

      // Build local dataGroup entries for all created variables
      let maxLoc = 0;
      const allDGBulk = { ...(dataGroup || {}), ...pendingEntries };
      Object.values(allDGBulk).forEach(q => {
        (q?.varName || []).forEach(v => {
          if (v.Location && v.Location.includes(':')) {
            const [, end] = v.Location.split(':').map(Number);
            if (end > maxLoc) maxLoc = end;
          }
        });
      });

      const newEntries = {};
      const newVarNames = [];

      createdVars.forEach((v, idx) => {
        newVarNames.push(v.varName);
        const loc      = maxLoc + idx + 1;
        const isNumeric = v.type !== 'boolean';
        newEntries[v.varName] = {
          'Question number': v.varName,
          Qtitle:       v.description || v.varName,
          instructions: '',
          type:         isNumeric ? 'Numeric' : 'SingleResponse',
          values:       isNumeric ? [] : [{ index: 0, title: 'No' }, { index: 1, title: 'Yes' }],
          valueLabels:  isNumeric ? {} : { '0.0': 'No', '1.0': 'Yes' },
          varName:      [{ Name: v.varName, Location: `${loc}:${loc + 1}` }],
          order:        Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
          _order:       Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
        };
      });

      setcurrentSelectedVariablesset(prev => [...prev, ...newVarNames]);
      setPendingEntries(prev => ({ ...prev, ...newEntries }));

      const errorCount = (data.errors || []).length;
      if (errorCount > 0) {
        toast.warning(
          `Imported ${createdVars.length} variable(s). ${errorCount} failed — see console for details.`
        );
        console.warn('[Bulk Import] partial errors:', data.errors);
      } else {
        toast.success(`Successfully imported ${createdVars.length} variable(s)!`);
      }
      // Modal stays open so the user can review and click "Save Structure" manually.
    } catch (error) {
      console.error('[Bulk Import] Error:', error);
      toast.error(error.message || 'Error importing variables');
    } finally {
      setIsSaving(false);
    }
  };

  const bulkValidCount = bulkRows.filter(r => r.varName.trim()).length;
  const bulkReadyCount = bulkRows.filter(r => r.varName.trim() && r.condition.trim()).length;
  const bulkPreviewRows = parseBulkPastedData(bulkPasteData);

  // ── Recode-variables helpers ───────────────────────────────────────────────
  const handleRecodeRowChange = (index, field, value) => {
    const updated = [...recodeRows];
    updated[index] = { ...updated[index], [field]: value };
    setRecodeRows(updated);
  };

  const handleRecodeAddRow = () =>
    setRecodeRows([...recodeRows, { newVarName: '', recodeSpec: '' }]);

  const handleRecodeRemoveRow = (index) => {
    if (recodeRows.length > 1) {
      const updated = [...recodeRows];
      updated.splice(index, 1);
      setRecodeRows(updated);
    }
  };

  const parseRecodePastedData = (text) => {
    const lines = text.trim().split(/\r?\n/);
    const parsed = [];
    for (const line of lines) {
      let cols = line.split(/\t/);
      if (cols.length === 1) cols = line.split(/,/);
      const newVarName = cols[0] ? cols[0].trim() : '';
      const recodeSpec = cols[1] ? cols[1].trim() : '';
      if (newVarName || recodeSpec) parsed.push({ newVarName, recodeSpec });
    }
    return parsed;
  };

  const handleApplyRecodePaste = () => {
    if (!recodePasteData.trim()) { toast.warning('Please paste some data first'); return; }
    const newRows = parseRecodePastedData(recodePasteData);
    if (newRows.length === 0) { toast.warning('No valid data found.'); return; }
    setRecodeRows(newRows);
    setShowRecodePasteModal(false);
    setRecodePasteData('');
    toast.success(`Parsed ${newRows.length} recode row(s) — review and save when ready.`);
  };

  const handleRecodeFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setRecodePasteData(content);
      const preview = parseRecodePastedData(content);
      if (preview.length > 0) toast.info(`Loaded ${preview.length} recode row(s) from file.`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleRecodeSave = async () => {
    const readyRows = recodeRows.filter(r => r.newVarName.trim() && r.recodeSpec.trim());
    if (readyRows.length === 0) {
      toast.warning('Please add at least one row with both a new variable name and a recode spec');
      return;
    }

    setIsSaving(true);
    try {
      const { data } = await apiService.post('/compute-recode-vars', {
        projectId,
        recodeVariables: readyRows.map(r => ({
          newVarName: r.newVarName.trim(),
          recodeSpec: r.recodeSpec.trim(),
        })),
        dataGroup,
        project: JSON.stringify(selectedProject),
        client:  JSON.stringify(selectedClient),
        user:    powner,
      }, { timeout: 300000 });

      if (!data || !data.success) {
        throw new Error(data?.error || 'Failed to recode variables');
      }

      const createdVars = data.created || [];

      let maxLoc = 0;
      const allDGRecode = { ...(dataGroup || {}), ...pendingEntries };
      Object.values(allDGRecode).forEach(q => {
        (q?.varName || []).forEach(v => {
          if (v.Location && v.Location.includes(':')) {
            const [, end] = v.Location.split(':').map(Number);
            if (end > maxLoc) maxLoc = end;
          }
        });
      });

      const newEntries = {};
      const newVarNames = [];

      createdVars.forEach((v, idx) => {
        newVarNames.push(v.varName);
        const loc = maxLoc + idx + 1;
        const targetValues = v.targetValues || [];
        const valueLabels  = v.valueLabels  || {};
        const values = targetValues.map((tv, i) => ({
          index: tv,
          title: valueLabels[String(tv)] || String(tv),
        }));
        newEntries[v.varName] = {
          'Question number': v.varName,
          Qtitle:       v.varName,
          instructions: '',
          type:         values.length > 0 ? 'SingleResponse' : 'Numeric',
          values,
          valueLabels:  Object.fromEntries(
            Object.entries(valueLabels).map(([k, lbl]) => [`${k}.0`, lbl])
          ),
          varName:  [{ Name: v.varName, Location: `${loc}:${loc + 1}` }],
          order:    Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
          _order:   Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
        };
      });

      setcurrentSelectedVariablesset(prev => [...prev, ...newVarNames]);
      setPendingEntries(prev => ({ ...prev, ...newEntries }));

      const errorCount = (data.errors || []).length;
      if (errorCount > 0) {
        toast.warning(`Recoded ${createdVars.length} variable(s). ${errorCount} failed — see console.`);
        console.warn('[Recode] partial errors:', data.errors);
      } else {
        toast.success(`Successfully recoded ${createdVars.length} variable(s)!`);
      }
    } catch (error) {
      console.error('[Recode] Error:', error);
      toast.error(error.message || 'Error recoding variables');
    } finally {
      setIsSaving(false);
    }
  };

  const recodeReadyCount  = recodeRows.filter(r => r.newVarName.trim() && r.recodeSpec.trim()).length;
  const recodePreviewRows = parseRecodePastedData(recodePasteData);

  // ── Conditional-compute helpers ────────────────────────────────────────────
  // Column order: Condition → Variable Name → Calculation → Description (optional)
  const handleConditionalRowChange = (index, field, value) => {
    const updated = [...conditionalRows];
    updated[index] = { ...updated[index], [field]: value };
    setConditionalRows(updated);
  };

  const handleConditionalAddRow = () =>
    setConditionalRows([...conditionalRows, { condition: '', varName: '', calculation: '', description: '' }]);

  const handleConditionalRemoveRow = (index) => {
    if (conditionalRows.length > 1) {
      const updated = [...conditionalRows];
      updated.splice(index, 1);
      setConditionalRows(updated);
    }
  };

  const parseConditionalPastedData = (text) => {
    const lines = text.trim().split(/\r?\n/);
    const parsedRows = [];
    for (const line of lines) {
      let columns = line.split(/\t/);
      if (columns.length === 1) columns = line.split(/,/);
      const condition   = columns[0] ? columns[0].trim() : '';
      const varName     = columns[1] ? columns[1].trim() : '';
      const calculation = columns[2] ? columns[2].trim() : '';
      const description = columns[3] ? columns[3].trim() : '';
      if (condition || varName || calculation) parsedRows.push({ condition, varName, calculation, description });
    }
    return parsedRows;
  };

  const handleApplyConditionalPaste = () => {
    if (!conditionalPasteData.trim()) { toast.warning('Please paste some data first'); return; }
    const newRows = parseConditionalPastedData(conditionalPasteData);
    if (newRows.length === 0) { toast.warning('No valid data found.'); return; }
    setConditionalRows(newRows);
    setShowConditionalPasteModal(false);
    setConditionalPasteData('');
    toast.success(`Parsed ${newRows.length} row(s) — review and save when ready.`);
  };

  const handleConditionalFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setConditionalPasteData(content);
      const preview = parseConditionalPastedData(content);
      if (preview.length > 0) toast.info(`Loaded ${preview.length} row(s) from file.`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleConditionalSave = async () => {
    const readyRows = conditionalRows.filter(r => r.condition.trim() && r.varName.trim() && r.calculation.trim());
    if (readyRows.length === 0) {
      toast.warning('Please add at least one row with a condition, variable name, and calculation');
      return;
    }

    setIsSaving(true);
    try {
      const { data } = await apiService.post('/compute-newvar-conditional', {
        projectId,
        conditionalVariables: readyRows.map(r => ({
          varName:     r.varName.trim(),
          condition:   r.condition.trim(),
          calculation: r.calculation.trim(),
          description: r.description.trim(),
        })),
        dataGroup,
        project: JSON.stringify(selectedProject),
        client:  JSON.stringify(selectedClient),
        user:    powner,
      }, { timeout: 300000 }); // 5-min timeout — SPSS write + OneDrive upload can be slow

      if (!data || !data.success) {
        throw new Error(data?.error || 'Failed to compute conditional variable(s)');
      }

      const createdVars = data.created || [];

      let maxLoc = 0;
      const allDGConditional = { ...(dataGroup || {}), ...pendingEntries };
      Object.values(allDGConditional).forEach(q => {
        (q?.varName || []).forEach(v => {
          if (v.Location && v.Location.includes(':')) {
            const [, end] = v.Location.split(':').map(Number);
            if (end > maxLoc) maxLoc = end;
          }
        });
      });

      const newEntries = {};
      const newVarNames = [];

      createdVars.forEach((v, idx) => {
        newVarNames.push(v.varName);
        const loc = maxLoc + idx + 1;
        newEntries[v.varName] = {
          'Question number': v.varName,
          Qtitle:       v.description || v.varName,
          instructions: '',
          type:         'Numeric',
          values:       [],
          valueLabels:  {},
          varName:      [{ Name: v.varName, Location: `${loc}:${loc + 1}` }],
          order:        Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
          _order:       Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
        };
      });

      setcurrentSelectedVariablesset(prev => [...prev, ...newVarNames]);
      setPendingEntries(prev => ({ ...prev, ...newEntries }));

      const errorCount = (data.errors || []).length;
      if (errorCount > 0) {
        toast.warning(`Computed ${createdVars.length} variable(s). ${errorCount} row(s)/variable(s) failed — see console.`);
        console.warn('[Conditional Compute] partial errors:', data.errors);
      } else {
        toast.success(`Successfully computed ${createdVars.length} variable(s)!`);
      }
    } catch (error) {
      console.error('[Conditional Compute] Error:', error);
      toast.error(error.message || 'Error computing conditional variable(s)');
    } finally {
      setIsSaving(false);
    }
  };

  // ── SPSS-compare save handler ──────────────────────────────────────────────
  const handleSpssCompareSave = async () => {
    const readyRows = spssRows.filter(r => r.varName.trim() && r.condition.trim());
    if (readyRows.length === 0) {
      toast.warning('Please add at least one row with a variable name and condition');
      return;
    }
    setIsSaving(true);
    try {
      const { data } = await apiService.post('/compute-newvar-spss-cond', {
        projectId,
        spssVariables: readyRows.map(r => ({
          varName:     r.varName.trim(),
          condition:   r.condition.trim(),
          value:       r.value.trim() || '1',
          description: r.description.trim(),
        })),
        project: JSON.stringify(selectedProject),
        client:  JSON.stringify(selectedClient),
        user:    powner,
      }, { timeout: 300000 });

      if (!data || !data.success) throw new Error(data?.error || 'Failed to compute variable(s)');

      const createdVars = data.created || [];
      let maxLoc = 0;
      const allDG = { ...(dataGroup || {}), ...pendingEntries };
      Object.values(allDG).forEach(q => {
        (q?.varName || []).forEach(v => {
          if (v.Location && v.Location.includes(':')) {
            const [, end] = v.Location.split(':').map(Number);
            if (end > maxLoc) maxLoc = end;
          }
        });
      });

      const newEntries = {};
      const newVarNames = [];
      createdVars.forEach((v, idx) => {
        newVarNames.push(v.varName);
        const loc = maxLoc + idx + 1;
        newEntries[v.varName] = {
          'Question number': v.varName,
          Qtitle:       v.description || v.varName,
          instructions: '',
          type:         'Numeric',
          values:       [],
          valueLabels:  {},
          varName:      [{ Name: v.varName, Location: `${loc}:${loc + 1}` }],
          order:        Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
          _order:       Object.keys(dataGroup || {}).length + Object.keys(pendingEntries).length + idx + 1,
        };
      });

      setcurrentSelectedVariablesset(prev => [...prev, ...newVarNames]);
      setPendingEntries(prev => ({ ...prev, ...newEntries }));

      const errorCount = (data.errors || []).length;
      if (errorCount > 0) {
        toast.warning(`Computed ${createdVars.length} variable(s). ${errorCount} failed — see console.`);
        console.warn('[SPSS Compare] errors:', data.errors);
      } else {
        toast.success(`Successfully computed ${createdVars.length} variable(s)!`);
      }
    } catch (error) {
      console.error('[SPSS Compare] Error:', error);
      toast.error(error.message || 'Error computing variable(s)');
    } finally {
      setIsSaving(false);
    }
  };

  const conditionalReadyRowCount = conditionalRows.filter(r => r.condition.trim() && r.varName.trim() && r.calculation.trim()).length;
  const conditionalVarCount = new Set(conditionalRows.filter(r => r.varName.trim()).map(r => r.varName.trim())).size;
  const conditionalPreviewRows = parseConditionalPastedData(conditionalPasteData);

  // ── SPSS-compare paste helpers ────────────────────────────────────────────
  const parseSpssPastedData = (text) => {
    const lines = text.trim().split(/\r?\n/);
    const parsed = [];
    for (const line of lines) {
      let cols = line.split(/\t/);
      if (cols.length === 1) cols = line.split(/,/);
      const varName     = cols[0] ? cols[0].trim() : '';
      const condition   = cols[1] ? cols[1].trim() : '';
      const value       = cols[2] ? cols[2].trim() : '1';
      const description = cols[3] ? cols[3].trim() : '';
      if (varName || condition) parsed.push({ varName, condition, value: value || '1', description });
    }
    return parsed;
  };

  const handleApplySpssPaste = () => {
    if (!spssPasteData.trim()) { toast.warning('Please paste some data first'); return; }
    const newRows = parseSpssPastedData(spssPasteData);
    if (newRows.length === 0) { toast.warning('No valid data found.'); return; }
    setSpssRows(newRows);
    setShowSpssPasteModal(false);
    setSpssPasteData('');
    toast.success(`Parsed ${newRows.length} row(s) — review and compute when ready.`);
  };

  const handleSpssFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setSpssPasteData(content);
      const preview = parseSpssPastedData(content);
      if (preview.length > 0) toast.info(`Loaded ${preview.length} row(s) from file.`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const spssPreviewRows = parseSpssPastedData(spssPasteData);
  const spssReadyCount = spssRows.filter(r => r.varName.trim() && r.condition.trim()).length;
  const pendingCount = Object.keys(pendingEntries).length;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-header">
          <h3>
            {recodeMode ? 'Recode Variables'
              : bulkMode ? 'Bulk Import Variables'
              : conditionalMode ? 'Conditional Compute'
              : spssCompareMode ? 'SPSS Condition (Compare Variables)'
              : 'Create Custom Variable'}
          </h3>
          <button onClick={onClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Mode Toggle ── */}
        <div className="mode-toggle-bar">
          <button
            className={`mode-tab ${!bulkMode && !recodeMode && !conditionalMode && !spssCompareMode ? 'active' : ''}`}
            onClick={() => { setBulkMode(false); setRecodeMode(false); setConditionalMode(false); setSpssCompareMode(false); }}
          >
            Single Variable
          </button>
          <button
            className={`mode-tab ${bulkMode ? 'active' : ''}`}
            onClick={() => { setBulkMode(true); setRecodeMode(false); setConditionalMode(false); setSpssCompareMode(false); }}
          >
            <FontAwesomeIcon icon={faTable} /> Bulk Import
          </button>
          <button
            className={`mode-tab ${recodeMode ? 'active' : ''}`}
            onClick={() => { setRecodeMode(true); setBulkMode(false); setConditionalMode(false); setSpssCompareMode(false); }}
          >
            <FontAwesomeIcon icon={faRandom} /> Recode Variables
          </button>
          <button
            className={`mode-tab ${conditionalMode ? 'active' : ''}`}
            onClick={() => { setConditionalMode(true); setBulkMode(false); setRecodeMode(false); setSpssCompareMode(false); }}
          >
            <FontAwesomeIcon icon={faCodeBranch} /> Conditional Compute
          </button>
          <button
            className={`mode-tab ${spssCompareMode ? 'active' : ''}`}
            onClick={() => { setSpssCompareMode(true); setConditionalMode(false); setBulkMode(false); setRecodeMode(false); }}
          >
            <FontAwesomeIcon icon={faCodeBranch} /> SPSS Condition
          </button>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SINGLE VARIABLE MODE (existing)
        ════════════════════════════════════════════════════════════════════ */}
        {!bulkMode && !recodeMode && !conditionalMode && !spssCompareMode && (
          <>
            <div className="variable-name-input">
              <label>Variable Name:</label>
              <input
                type="text"
                value={variableName}
                onChange={(e) => setVariableName(e.target.value)}
                placeholder="Enter variable name"
              />
            </div>

            <label className="toggle-item">
              <input type="checkbox" checked={loopVar} onChange={(e) => setLoopVar(e.target.checked)} />
              <span className="toggle-slider"></span>
              <span className="toggle-text">Bilendi Setup</span>
            </label>

            {loopVar && (
              <div className="variable-name-input">
                <label>Loop by Name:</label>
                <input
                  type="text"
                  value={loopVarName}
                  onChange={(e) => setLoopVarName(e.target.value)}
                  placeholder="Enter variable name"
                />
              </div>
            )}

            <div className="table-container">
              <div className="table-toolbar">
                <button onClick={() => setShowPasteModal(true)} className="paste-excel-btn" title="Paste from Excel">
                  <FontAwesomeIcon icon={faPaste} /> Paste from Excel
                </button>
              </div>
              <table className="custom-var-table">
                <thead>
                  <tr>
                    <th>Group Label</th>
                    <th>Logic Definition</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={row.stub}
                          onChange={(e) => handleInputChange(index, 'stub', e.target.value)}
                          placeholder="Group label"
                        />
                      </td>
                      <td>
                        <div className="logic-input-container">
                          <input
                            type="text"
                            value={row.logic}
                            onChange={(e) => handleInputChange(index, 'logic', e.target.value)}
                            placeholder="e.g., Q1 or Q2"
                          />
                          <button className="variable-selector-btn" onClick={() => openVariableSelector(index)} title="Select variables">
                            <FontAwesomeIcon icon={faCode} />
                          </button>
                        </div>
                      </td>
                      <td className="actions-cell">
                        <button onClick={() => handleMoveUp(index)} disabled={index === 0} title="Move up">
                          <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                        <button onClick={() => handleMoveDown(index)} disabled={index === rows.length - 1} title="Move down">
                          <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                        <button onClick={() => handleRemoveRow(index)} disabled={rows.length <= 1} title="Remove row" className="remove-btn">
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-footer">
              <div className="footer-buttons">
                <button onClick={handleAddRow} className="add-row-btn" disabled={isSaving}>
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </button>
                <button onClick={handleSave} className={`save-btn ${isSaving ? 'saving' : ''}`} disabled={isSaving}>
                  {isSaving ? (<><span className="spinner"></span> Saving...</>) : 'Save Custom Variable'}
                </button>
                <button
                  onClick={handleSaveDataGroup}
                  className={`save-dg-btn ${isSavingDG ? 'saving' : ''}`}
                  disabled={isSavingDG || isSaving}
                  title="Save the current variable structure (dataGroup JSON) to the database"
                >
                  {isSavingDG
                    ? <><span className="spinner"></span> Saving…</>
                    : <><FontAwesomeIcon icon={faDatabase} /> Save Structure{pendingCount > 0 ? ` (${pendingCount})` : ''}</>}
                </button>
              </div>
              {saveError && (
                <div className="error-message">
                  <span className="error-text">{saveError}</span>
                  <button onClick={retrySave} className="retry-btn">Retry</button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BULK IMPORT MODE (new)
        ════════════════════════════════════════════════════════════════════ */}
        {bulkMode && !recodeMode && (
          <>
            {/* Instructions banner */}
            <div className="bulk-instructions-banner">
              <div className="bulk-col-legend">
                <div className="bulk-col-pill col1">Col 1 — Variable Name <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col2">Col 2 — Condition <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col3">Col 3 — Description <span className="opt-tag">optional</span></div>
              </div>
              <p className="bulk-hint">Each row in Excel becomes a separate new variable.</p>
            </div>

            {/* Toolbar */}
            <div className="bulk-toolbar">
              <button onClick={() => setShowBulkPasteModal(true)} className="paste-excel-btn">
                <FontAwesomeIcon icon={faPaste} /> Paste from Excel
              </button>
              <span className="bulk-count-badge">
                {bulkReadyCount} ready &nbsp;·&nbsp; {bulkValidCount} named &nbsp;·&nbsp; {bulkRows.length} total
              </span>
            </div>

            {/* Editable bulk table */}
            <div className="bulk-table-wrapper">
              <table className="bulk-var-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-varname">Variable Name <span className="req-star">*</span></th>
                    <th className="col-condition">Condition <span className="req-star">*</span></th>
                    <th className="col-description">Description <span className="opt-tag">optional</span></th>
                    <th className="col-actions">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkRows.map((row, index) => (
                    <tr key={index} className={!row.varName.trim() ? 'bulk-row-empty' : ''}>
                      <td className="col-num">{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={row.varName}
                          onChange={(e) => handleBulkRowChange(index, 'varName', e.target.value)}
                          placeholder="e.g. AgeGroup"
                          className={!row.varName.trim() ? 'input-warn' : ''}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.condition}
                          onChange={(e) => handleBulkRowChange(index, 'condition', e.target.value)}
                          placeholder="e.g. Q1=1 or Q2=2"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.description}
                          onChange={(e) => handleBulkRowChange(index, 'description', e.target.value)}
                          placeholder="Optional description…"
                          className="desc-input"
                        />
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleBulkRemoveRow(index)}
                          disabled={bulkRows.length <= 1}
                          title="Remove row"
                          className="remove-btn"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bulk footer */}
            <div className="modal-footer">
              <div className="footer-buttons">
                <button onClick={handleBulkAddRow} className="add-row-btn">
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </button>
                <button
                  onClick={handleBulkSave}
                  className={`save-btn bulk-save-btn ${isSaving ? 'saving' : ''}`}
                  disabled={bulkReadyCount === 0 || isSaving}
                  title={bulkReadyCount === 0 ? 'Each variable needs a name and a condition' : ''}
                >
                  {isSaving ? (
                    <><span className="spinner"></span> Importing…</>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFileImport} />&nbsp;
                      Import {bulkReadyCount > 0 ? `${bulkReadyCount} ` : ''}Variable{bulkReadyCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
                <button
                  onClick={handleSaveDataGroup}
                  className={`save-dg-btn ${isSavingDG ? 'saving' : ''}`}
                  disabled={isSavingDG || isSaving}
                  title="Save the current variable structure (dataGroup JSON) to the database"
                >
                  {isSavingDG
                    ? <><span className="spinner"></span> Saving…</>
                    : <><FontAwesomeIcon icon={faDatabase} /> Save Structure{pendingCount > 0 ? ` (${pendingCount})` : ''}</>}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            RECODE VARIABLES MODE
        ════════════════════════════════════════════════════════════════════ */}
        {recodeMode && (
          <>
            {/* Instructions banner */}
            <div className="bulk-instructions-banner">
              <div className="bulk-col-legend">
                <div className="bulk-col-pill col1">Col 1 — New Variable Name <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col2">Col 2 — Recode Spec <span className="req-star">*</span></div>
              </div>
              <p className="bulk-hint">
                Format: <code>SourceVar(val=newval;lo-hi=newval;v1,v2=newval)</code>
                &nbsp;e.g.&nbsp;<code>Q4(1=0;2-6=1;7=2;8,9=3)</code>
                &nbsp;· Use <code>;</code> to separate rules · Use <code>-</code> for ranges · Use <code>,</code> for multiple values
              </p>
            </div>

            {/* Toolbar */}
            <div className="bulk-toolbar">
              <button onClick={() => setShowRecodePasteModal(true)} className="paste-excel-btn">
                <FontAwesomeIcon icon={faPaste} /> Paste from Excel
              </button>
              <span className="bulk-count-badge">
                {recodeReadyCount} ready &nbsp;·&nbsp; {recodeRows.length} total
              </span>
            </div>

            {/* Editable recode table */}
            <div className="bulk-table-wrapper">
              <table className="bulk-var-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-varname">New Variable Name <span className="req-star">*</span></th>
                    <th className="col-recode">Recode Spec <span className="req-star">*</span></th>
                    <th className="col-actions">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {recodeRows.map((row, index) => (
                    <tr key={index} className={!row.newVarName.trim() ? 'bulk-row-empty' : ''}>
                      <td className="col-num">{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={row.newVarName}
                          onChange={(e) => handleRecodeRowChange(index, 'newVarName', e.target.value)}
                          placeholder="e.g. AgeRec"
                          className={!row.newVarName.trim() ? 'input-warn' : ''}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.recodeSpec}
                          onChange={(e) => handleRecodeRowChange(index, 'recodeSpec', e.target.value)}
                          placeholder="e.g. Q4(1=0;2-6=1;7=2;8,9=3)"
                        />
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleRecodeRemoveRow(index)}
                          disabled={recodeRows.length <= 1}
                          title="Remove row"
                          className="remove-btn"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Recode footer */}
            <div className="modal-footer">
              <div className="footer-buttons">
                <button onClick={handleRecodeAddRow} className="add-row-btn">
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </button>
                <button
                  onClick={handleRecodeSave}
                  className={`save-btn bulk-save-btn ${isSaving ? 'saving' : ''}`}
                  disabled={recodeReadyCount === 0 || isSaving}
                  title={recodeReadyCount === 0 ? 'Each row needs a name and a recode spec' : ''}
                >
                  {isSaving ? (
                    <><span className="spinner"></span> Recoding…</>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faRandom} />&nbsp;
                      Recode {recodeReadyCount > 0 ? `${recodeReadyCount} ` : ''}Variable{recodeReadyCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
                <button
                  onClick={handleSaveDataGroup}
                  className={`save-dg-btn ${isSavingDG ? 'saving' : ''}`}
                  disabled={isSavingDG || isSaving}
                  title="Save the current variable structure (dataGroup JSON) to the database"
                >
                  {isSavingDG
                    ? <><span className="spinner"></span> Saving…</>
                    : <><FontAwesomeIcon icon={faDatabase} /> Save Structure{pendingCount > 0 ? ` (${pendingCount})` : ''}</>}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            CONDITIONAL COMPUTE MODE
            Builds one variable out of several condition -> calculation rules
            (an IF / ELSE IF chain). Rows sharing the same Variable Name are
            grouped server-side; the first matching condition wins.
        ════════════════════════════════════════════════════════════════════ */}
        {conditionalMode && (
          <>
            {/* Instructions banner */}
            <div className="bulk-instructions-banner">
              <div className="bulk-col-legend">
                <div className="bulk-col-pill col1">Col 1 — Condition <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col2">Col 2 — Variable Name <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col3">Col 3 — Calculation <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col4">Col 4 — Description <span className="opt-tag">optional</span></div>
              </div>
              <p className="bulk-hint">
                Rows with the same Variable Name are treated as one IF / ELSE IF chain — the first matching condition wins.
                &nbsp;e.g.&nbsp;<code>rec_C1_3a=1</code> → <code>C1_3a_month</code> → <code>C1_3a*4</code>,
                &nbsp;<code>rec_C1_3a=2</code> → <code>C1_3a_month</code> → <code>C1_3a</code>,
                &nbsp;<code>rec_C1_3a=3</code> → <code>C1_3a_month</code> → <code>C1_3a/3</code>
              </p>
            </div>

            {/* Toolbar */}
            <div className="bulk-toolbar">
              <button onClick={() => setShowConditionalPasteModal(true)} className="paste-excel-btn">
                <FontAwesomeIcon icon={faPaste} /> Paste from Excel
              </button>
              <span className="bulk-count-badge">
                {conditionalReadyRowCount} ready &nbsp;·&nbsp; {conditionalVarCount} variable(s) &nbsp;·&nbsp; {conditionalRows.length} row(s)
              </span>
            </div>

            {/* Editable conditional table */}
            <div className="bulk-table-wrapper">
              <table className="bulk-var-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-condition">Condition <span className="req-star">*</span></th>
                    <th className="col-varname">Variable Name <span className="req-star">*</span></th>
                    <th className="col-calculation">Calculation <span className="req-star">*</span></th>
                    <th className="col-description">Description <span className="opt-tag">optional</span></th>
                    <th className="col-actions">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {conditionalRows.map((row, index) => (
                    <tr key={index} className={!row.varName.trim() ? 'bulk-row-empty' : ''}>
                      <td className="col-num">{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={row.condition}
                          onChange={(e) => handleConditionalRowChange(index, 'condition', e.target.value)}
                          placeholder="e.g. rec_C1_3a=1"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.varName}
                          onChange={(e) => handleConditionalRowChange(index, 'varName', e.target.value)}
                          placeholder="e.g. C1_3a_month"
                          className={!row.varName.trim() ? 'input-warn' : ''}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.calculation}
                          onChange={(e) => handleConditionalRowChange(index, 'calculation', e.target.value)}
                          placeholder="e.g. C1_3a*4"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.description}
                          onChange={(e) => handleConditionalRowChange(index, 'description', e.target.value)}
                          placeholder="Optional description…"
                          className="desc-input"
                        />
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleConditionalRemoveRow(index)}
                          disabled={conditionalRows.length <= 1}
                          title="Remove row"
                          className="remove-btn"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Conditional footer */}
            <div className="modal-footer">
              <div className="footer-buttons">
                <button onClick={handleConditionalAddRow} className="add-row-btn">
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </button>
                <button
                  onClick={handleConditionalSave}
                  className={`save-btn bulk-save-btn ${isSaving ? 'saving' : ''}`}
                  disabled={conditionalReadyRowCount === 0 || isSaving}
                  title={conditionalReadyRowCount === 0 ? 'Each row needs a condition, a variable name, and a calculation' : ''}
                >
                  {isSaving ? (
                    <><span className="spinner"></span> Computing…</>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCodeBranch} />&nbsp;
                      Compute {conditionalVarCount > 0 ? `${conditionalVarCount} ` : ''}Variable{conditionalVarCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
                <button
                  onClick={handleSaveDataGroup}
                  className={`save-dg-btn ${isSavingDG ? 'saving' : ''}`}
                  disabled={isSavingDG || isSaving}
                  title="Save the current variable structure (dataGroup JSON) to the database"
                >
                  {isSavingDG
                    ? <><span className="spinner"></span> Saving…</>
                    : <><FontAwesomeIcon icon={faDatabase} /> Save Structure{pendingCount > 0 ? ` (${pendingCount})` : ''}</>}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            CONDITIONAL PASTE MODAL
        ════════════════════════════════════════════════════════════════════ */}
        {showConditionalPasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste Conditional Compute Rows from Excel</h3>

              <div className="paste-instructions">
                <p><strong>Expected column order:</strong></p>
                <div className="bulk-col-legend" style={{ margin: '8px 0' }}>
                  <div className="bulk-col-pill col1">Col 1 — Condition</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col2">Col 2 — Variable Name</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col3">Col 3 — Calculation</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col4">Col 4 — Description (optional)</div>
                </div>
                <ul>
                  <li>Rows sharing the same Variable Name form one IF / ELSE IF chain — the first matching condition wins</li>
                  <li>Example: <code>rec_C1_3a=1</code> / <code>C1_3a_month</code> / <code>C1_3a*4</code></li>
                  <li>Tab-separated (Excel default) or comma-separated are both supported</li>
                </ul>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={conditionalPasteData}
                  onChange={(e) => setConditionalPasteData(e.target.value)}
                  placeholder={
                    'Paste Excel data here…\n\nExample:\n' +
                    'rec_C1_3a=1\tC1_3a_month\tC1_3a*4\n' +
                    'rec_C1_3a=2\tC1_3a_month\tC1_3a\n' +
                    'rec_C1_3a=3\tC1_3a_month\tC1_3a/3'
                  }
                  rows={9}
                />
              </div>

              <div className="file-upload-section">
                <input
                  type="file"
                  ref={conditionalFileInputRef}
                  onChange={handleConditionalFileUpload}
                  accept=".csv,.tsv,.txt"
                  style={{ display: 'none' }}
                />
                <button type="button" onClick={() => conditionalFileInputRef.current.click()} className="upload-file-btn">
                  Or Upload CSV / TSV File
                </button>
              </div>

              {/* Live preview */}
              {conditionalPasteData.trim() && (
                <div className="paste-preview">
                  <h4>Preview ({conditionalPreviewRows.length} row{conditionalPreviewRows.length !== 1 ? 's' : ''} detected):</h4>
                  <div className="preview-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Condition</th>
                          <th>Variable Name</th>
                          <th>Calculation</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conditionalPreviewRows.slice(0, 6).map((row, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className={!row.condition ? 'cell-empty' : ''}>{row.condition || '—'}</td>
                            <td className={!row.varName ? 'cell-empty' : ''}>{row.varName || '—'}</td>
                            <td className={!row.calculation ? 'cell-empty' : ''}>{row.calculation || '—'}</td>
                            <td className="cell-desc">{row.description || <em style={{ color: '#aaa' }}>—</em>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {conditionalPreviewRows.length > 6 && (
                      <p className="preview-more">+ {conditionalPreviewRows.length - 6} more row(s)</p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => { setShowConditionalPasteModal(false); setConditionalPasteData(''); }} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleApplyConditionalPaste} className="apply-btn" disabled={!conditionalPasteData.trim()}>
                  Apply — Load {conditionalPreviewRows.length > 0 ? conditionalPreviewRows.length : ''} Row{conditionalPreviewRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SPSS CONDITION MODE
            Supports SPSS-style conditions: ANY(), ~=, column=column.
        ════════════════════════════════════════════════════════════════════ */}
        {spssCompareMode && (
          <>
            <div className="bulk-instructions-banner">
              <strong>SPSS Condition</strong> — write conditions exactly as in SPSS syntax.
              Supports <code>ANY(var,1,2,3)</code>, <code>var~=var2</code>, <code>Q1=Q2</code> (column compare), <code>AND</code> / <code>OR</code> / <code>NOT</code>.
              The variable is set to <em>Value</em> for every respondent where the condition is true.
              <div className="bulk-col-legend" style={{ marginTop: '8px' }}>
                <div className="bulk-col-pill col1">Col 1 — Variable Name <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col2">Col 2 — SPSS Condition <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col3">Col 3 — Value <span className="opt-tag">optional, default 1</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col4">Col 4 — Description <span className="opt-tag">optional</span></div>
              </div>
            </div>

            <div className="bulk-toolbar">
              <button onClick={() => setShowSpssPasteModal(true)} className="paste-excel-btn">
                <FontAwesomeIcon icon={faPaste} /> Paste from Excel
              </button>
              <span className="bulk-count-badge">
                {spssReadyCount} ready &nbsp;·&nbsp; {spssRows.length} total
              </span>
            </div>

            <div className="table-container">
              <table className="custom-var-table">
                <thead>
                  <tr>
                    <th>Variable Name</th>
                    <th>SPSS Condition</th>
                    <th>Value</th>
                    <th>Description</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {spssRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="text"
                          value={row.varName}
                          onChange={e => {
                            const r = [...spssRows]; r[idx] = { ...r[idx], varName: e.target.value }; setSpssRows(r);
                          }}
                          placeholder="e.g. Secure"
                          style={{ width: '110px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.condition}
                          onChange={e => {
                            const r = [...spssRows]; r[idx] = { ...r[idx], condition: e.target.value }; setSpssRows(r);
                          }}
                          placeholder="e.g. (Q1_rec=Q2) and ANY(Q3,1,2,3)"
                          style={{ width: '340px', fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.value}
                          onChange={e => {
                            const r = [...spssRows]; r[idx] = { ...r[idx], value: e.target.value }; setSpssRows(r);
                          }}
                          placeholder="1"
                          style={{ width: '55px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.description}
                          onChange={e => {
                            const r = [...spssRows]; r[idx] = { ...r[idx], description: e.target.value }; setSpssRows(r);
                          }}
                          placeholder="Label (optional)"
                          style={{ width: '150px' }}
                        />
                      </td>
                      <td>
                        <button
                          className="remove-row-btn"
                          onClick={() => setSpssRows(spssRows.filter((_, i) => i !== idx))}
                          title="Remove row"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                className="add-row-btn"
                onClick={() => setSpssRows([...spssRows, { varName: '', condition: '', value: '1', description: '' }])}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Row
              </button>
            </div>

            <div className="modal-footer">
              <div className="footer-buttons">
                <button
                  onClick={handleSpssCompareSave}
                  className={`save-btn ${isSaving ? 'saving' : ''}`}
                  disabled={isSaving || spssReadyCount === 0}
                  title={spssReadyCount === 0 ? 'Each row needs a variable name and a condition' : ''}
                >
                  {isSaving
                    ? <><span className="spinner"></span> Computing…</>
                    : `Compute ${spssReadyCount > 0 ? `${spssReadyCount} ` : ''}Variable${spssReadyCount !== 1 ? 's' : ''}`}
                </button>
                <button
                  onClick={handleSaveDataGroup}
                  className={`save-dg-btn ${isSavingDG ? 'saving' : ''}`}
                  disabled={isSavingDG || isSaving}
                  title="Save the current variable structure (dataGroup JSON) to the database"
                >
                  {isSavingDG
                    ? <><span className="spinner"></span> Saving…</>
                    : <><FontAwesomeIcon icon={faDatabase} /> Save Structure{pendingCount > 0 ? ` (${pendingCount})` : ''}</>}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SPSS CONDITION PASTE MODAL
        ════════════════════════════════════════════════════════════════════ */}
        {showSpssPasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste SPSS Condition Rows from Excel</h3>

              <div className="paste-instructions">
                <p><strong>Expected column order:</strong></p>
                <div className="bulk-col-legend" style={{ margin: '8px 0' }}>
                  <div className="bulk-col-pill col1">Col 1 — Variable Name</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col2">Col 2 — SPSS Condition</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col3">Col 3 — Value (optional, default 1)</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col4">Col 4 — Description (optional)</div>
                </div>
                <ul>
                  <li>Supports <code>ANY(var,1,2,3)</code>, <code>var~=var2</code>, <code>Q1=Q2</code>, <code>AND</code>/<code>OR</code>/<code>NOT</code></li>
                  <li>Value column is optional — leave blank to default to <code>1</code></li>
                  <li>Tab-separated (Excel default) or comma-separated are both supported</li>
                </ul>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={spssPasteData}
                  onChange={(e) => setSpssPasteData(e.target.value)}
                  placeholder={
                    'Paste Excel data here…\n\nExample:\n' +
                    'Secure\t(Q1_rec=Q2) and ANY(Q3,1,2,3)\t1\tSecure segment\n' +
                    'HighAge\tQ_age>=35\t1\n' +
                    'CompareSeg\tQ5~=Q6\t2\tDifferent values'
                  }
                  rows={9}
                />
              </div>

              <div className="file-upload-section">
                <input
                  type="file"
                  ref={spssFileInputRef}
                  onChange={handleSpssFileUpload}
                  accept=".csv,.tsv,.txt"
                  style={{ display: 'none' }}
                />
                <button type="button" onClick={() => spssFileInputRef.current.click()} className="upload-file-btn">
                  Or Upload CSV / TSV File
                </button>
              </div>

              {spssPasteData.trim() && (
                <div className="paste-preview">
                  <h4>Preview ({spssPreviewRows.length} row{spssPreviewRows.length !== 1 ? 's' : ''} detected):</h4>
                  <div className="preview-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Variable Name</th>
                          <th>SPSS Condition</th>
                          <th>Value</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spssPreviewRows.slice(0, 6).map((row, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className={!row.varName ? 'cell-empty' : ''}>{row.varName || '—'}</td>
                            <td className={!row.condition ? 'cell-empty' : ''}>{row.condition || '—'}</td>
                            <td>{row.value || '1'}</td>
                            <td className="cell-desc">{row.description || <em style={{ color: '#aaa' }}>—</em>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {spssPreviewRows.length > 6 && (
                      <p className="preview-more">+ {spssPreviewRows.length - 6} more row(s)</p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => { setShowSpssPasteModal(false); setSpssPasteData(''); }} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleApplySpssPaste} className="apply-btn" disabled={!spssPasteData.trim()}>
                  Apply — Load {spssPreviewRows.length > 0 ? spssPreviewRows.length : ''} Row{spssPreviewRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            RECODE PASTE MODAL
        ════════════════════════════════════════════════════════════════════ */}
        {showRecodePasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste Recode Variables from Excel</h3>

              <div className="paste-instructions">
                <p><strong>Expected column order:</strong></p>
                <div className="bulk-col-legend" style={{ margin: '8px 0' }}>
                  <div className="bulk-col-pill col1">Col 1 — New Variable Name</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col2">Col 2 — Recode Spec</div>
                </div>
                <ul>
                  <li>Recode spec format: <code>SourceVar(val=newval;lo-hi=newval;v1,v2=newval)</code></li>
                  <li>Example: <code>Q4(1=0;2-6=1;7=2;8,9=3)</code></li>
                  <li>Tab-separated (Excel default) or comma-separated are both supported</li>
                </ul>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={recodePasteData}
                  onChange={(e) => setRecodePasteData(e.target.value)}
                  placeholder={'Paste Excel data here…\n\nExample:\nAgeRec\tQ1(1=0;2-6=1;7=2;8,9=3)\nIncRec\tQ5(1=1;2=1;3=2;4=2;5=3)'}
                  rows={9}
                />
              </div>

              <div className="file-upload-section">
                <input
                  type="file"
                  ref={recodeFileInputRef}
                  onChange={handleRecodeFileUpload}
                  accept=".csv,.tsv,.txt"
                  style={{ display: 'none' }}
                />
                <button type="button" onClick={() => recodeFileInputRef.current.click()} className="upload-file-btn">
                  Or Upload CSV / TSV File
                </button>
              </div>

              {/* Live preview */}
              {recodePasteData.trim() && (
                <div className="paste-preview">
                  <h4>Preview ({recodePreviewRows.length} row{recodePreviewRows.length !== 1 ? 's' : ''} detected):</h4>
                  <div className="preview-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>New Variable Name</th>
                          <th>Recode Spec</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recodePreviewRows.slice(0, 6).map((row, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className={!row.newVarName ? 'cell-empty' : ''}>{row.newVarName || '—'}</td>
                            <td className={!row.recodeSpec ? 'cell-empty' : ''}>{row.recodeSpec || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {recodePreviewRows.length > 6 && (
                      <p className="preview-more">+ {recodePreviewRows.length - 6} more row(s)</p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => { setShowRecodePasteModal(false); setRecodePasteData(''); }} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleApplyRecodePaste} className="apply-btn" disabled={!recodePasteData.trim()}>
                  Apply — Load {recodePreviewRows.length > 0 ? recodePreviewRows.length : ''} Row{recodePreviewRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BULK PASTE MODAL
        ════════════════════════════════════════════════════════════════════ */}
        {showBulkPasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste Variables from Excel</h3>

              <div className="paste-instructions">
                <p><strong>Expected column order:</strong></p>
                <div className="bulk-col-legend" style={{ margin: '8px 0' }}>
                  <div className="bulk-col-pill col1">Col 1 — Variable Name</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col2">Col 2 — Condition</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col3">Col 3 — Description (optional)</div>
                </div>
                <ul>
                  <li>Select and copy the cells from Excel, then paste below</li>
                  <li>Each row = one new variable</li>
                  <li>Tab-separated (Excel default) or comma-separated are both supported</li>
                </ul>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={bulkPasteData}
                  onChange={(e) => setBulkPasteData(e.target.value)}
                  placeholder={
                    'Paste Excel data here…\n\nExample:\n' +
                    'AgeGroup\t18-35 respondents\tYounger segment\n' +
                    'OlderGroup\t36+ respondents\n' +
                    'HighIncome\tIncome=1 or Income=2'
                  }
                  rows={9}
                />
              </div>

              <div className="file-upload-section">
                <input
                  type="file"
                  ref={bulkFileInputRef}
                  onChange={handleBulkFileUpload}
                  accept=".csv,.tsv,.txt"
                  style={{ display: 'none' }}
                />
                <button type="button" onClick={() => bulkFileInputRef.current.click()} className="upload-file-btn">
                  Or Upload CSV / TSV File
                </button>
              </div>

              {/* Live preview */}
              {bulkPasteData.trim() && (
                <div className="paste-preview">
                  <h4>Preview ({bulkPreviewRows.length} variable{bulkPreviewRows.length !== 1 ? 's' : ''} detected):</h4>
                  <div className="preview-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Variable Name</th>
                          <th>Condition</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreviewRows.slice(0, 6).map((row, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className={!row.varName ? 'cell-empty' : ''}>{row.varName || '—'}</td>
                            <td className={!row.condition ? 'cell-empty' : ''}>{row.condition || '—'}</td>
                            <td className="cell-desc">{row.description || <em style={{ color: '#aaa' }}>—</em>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkPreviewRows.length > 6 && (
                      <p className="preview-more">+ {bulkPreviewRows.length - 6} more row(s)</p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => { setShowBulkPasteModal(false); setBulkPasteData(''); }} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleApplyBulkPaste} className="apply-btn" disabled={!bulkPasteData.trim()}>
                  Apply — Load {bulkPreviewRows.length > 0 ? bulkPreviewRows.length : ''} Variable{bulkPreviewRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SINGLE-VAR PASTE MODAL (existing, unchanged)
        ════════════════════════════════════════════════════════════════════ */}
        {showPasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste from Excel</h3>

              <div className="paste-instructions">
                <p><strong>Instructions:</strong></p>
                <ul>
                  <li>Copy cells from Excel (2 columns: Group Label and Logic Definition)</li>
                  <li>Paste the data in the text area below</li>
                  <li>Click "Apply Paste" to add the rows</li>
                  <li>You can also upload a CSV or TSV file</li>
                </ul>
                <p><small>Tip: Excel data is copied as tab-separated values</small></p>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={pasteData}
                  onChange={(e) => setPasteData(e.target.value)}
                  placeholder="Paste your Excel data here...&#10;&#10;Example:&#10;Group 1&#9;Q1 or Q2&#10;Group 2&#9;Q3 and Q4&#10;Group 3&#9;not Q5"
                  rows={8}
                />
              </div>

              <div className="file-upload-section">
                <label className="file-upload-label">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".csv,.tsv,.txt,.xlsx"
                    style={{ display: 'none' }}
                  />
                  <button type="button" onClick={() => fileInputRef.current.click()} className="upload-file-btn">
                    Or Upload File
                  </button>
                </label>
              </div>

              {pasteData && (
                <div className="paste-preview">
                  <h4>Preview (first 5 rows):</h4>
                  <div className="preview-scroll">
                    <table>
                      <thead>
                        <tr><th>Group Label</th><th>Logic Definition</th></tr>
                      </thead>
                      <tbody>
                        {parsePastedData(pasteData).slice(0, 5).map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.stub || '(empty)'}</td>
                            <td>{row.logic || '(empty)'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsePastedData(pasteData).length > 5 && (
                      <p className="preview-more">... and {parsePastedData(pasteData).length - 5} more row(s)</p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => { setShowPasteModal(false); setPasteData(''); }} className="cancel-btn">Cancel</button>
                <button onClick={handlePasteFromExcel} className="apply-btn">Apply Paste</button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            VARIABLE SELECTOR MODAL (existing, unchanged)
        ════════════════════════════════════════════════════════════════════ */}
        {showVariableSelector && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content">
              <h3>Combine Variables</h3>

              <div className="operation-selector">
                <label>
                  <input type="radio" checked={!advancedMode} onChange={() => setAdvancedMode(false)} />
                  Simple Mode
                </label>
                <label>
                  <input type="radio" checked={advancedMode} onChange={() => setAdvancedMode(true)} />
                  Advanced Mode
                </label>
              </div>

              {!advancedMode ? (
                <>
                  <div className="operation-type">
                    <select value={operationType} onChange={(e) => setOperationType(e.target.value)}>
                      <option value="OR">OR (Any of the selected)</option>
                      <option value="AND">AND (All of the selected)</option>
                      <option value="NOT">AND NOT (All except last)</option>
                    </select>
                  </div>

                  <div className="variable-list">
                    {availableVariables.map(variable => (
                      <div
                        key={variable}
                        className={`variable-item ${selectedVariables.includes(variable) ? 'selected' : ''}`}
                        onClick={() => toggleVariableSelection(variable)}
                      >
                        {variable}
                      </div>
                    ))}
                  </div>

                  <div className="preview-section">
                    <h4>Preview:</h4>
                    <div className="preview-logic">
                      {selectedVariables.length > 0 ? (
                        selectedVariables.map((v, i) => (
                          <React.Fragment key={v}>
                            {i > 0 && (
                              <span className="operator">
                                {operationType === 'OR' ? 'OR' : operationType === 'AND' ? 'AND' : i === selectedVariables.length - 1 ? 'AND NOT' : 'AND'}
                              </span>
                            )}
                            <span className="variable">{v}</span>
                          </React.Fragment>
                        ))
                      ) : (
                        <span className="no-variables">No variables selected</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="advanced-mode">
                  <h4>Advanced Logic Builder</h4>
                  <textarea
                    value={rows[currentRowIndex].logic}
                    onChange={(e) => handleInputChange(currentRowIndex, 'logic', e.target.value)}
                    placeholder="Enter custom logic (e.g., (Q1 or Q2) and not Q3)"
                    rows={5}
                  />
                  <div className="available-vars-hint">
                    <p>Available variables: {availableVariables.join(', ')}</p>
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => setShowVariableSelector(false)} className="cancel-btn">Cancel</button>
                <button onClick={applyVariableOperation} className="apply-btn">Apply</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomizedVariable;
